import { NativeSelect, Stack, Text } from '@mantine/core';
import type { NewsletterOptIn } from '@quizee/core';
import type { Inherited } from '../brand/inheritance';
import { OverrideField } from './OverrideField';
import { ReadList, ReadRow, sourceWords } from './ReadValue';

/**
 * How signups are taken here, and where that was decided.
 *
 * This is the setting where naming the layer matters most. A brand on double opt in because
 * nobody has said otherwise and a brand on double opt in because somebody chose it look identical
 * to a reader and are not the same thing at all: change the publisher's default and only the first
 * one moves.
 */
export function NewsletterRead({
  value,
  inherited,
  scope,
}: {
  value: NewsletterOptIn | null;
  inherited: Inherited<NewsletterOptIn>;
  scope: 'org' | 'brand';
}) {
  const shown = value ?? inherited.value;

  return (
    <ReadList>
      <ReadRow
        label="Opt in mode"
        value={shown === 'double' ? 'Double opt in' : 'Single opt in'}
        source={sourceWords(value !== null, inherited.from)}
        note={
          <>
            {shown === 'double'
              ? 'Nobody joins this list without opening an email first, so an address typed by somebody else, or mistyped, never ends up on it. Signups sit as waiting to confirm until then, and some readers never do.'
              : 'Every address joins the list straight away, so nothing is lost between typing and confirming. It also means an address nobody has checked, including one typed by somebody who does not own it.'}
            {scope === 'org' &&
              ' A brand that has answered for itself keeps its own answer whatever this becomes.'}
          </>
        }
      />
    </ReadList>
  );
}

/**
 * How a signup is taken, and what each answer costs.
 *
 * It sits with the consent notice on both screens that carry it, because it is the same decision
 * one step further on: the notice says what a reader is agreeing to, this says what happens to the
 * address they hand over. Two answers, described by what a reader lives through and what ends up
 * on the list rather than by their names, because "single" and "double" tell somebody choosing
 * between them nothing at all.
 *
 * Written as inherited or set here for the same reason the theme is: a level that has never been
 * near this control is on whatever the layer below says, and an editor cannot tell that from a
 * value somebody chose. It matters more here than for a colour, so going back to inherited names
 * the layer that would actually answer.
 */
export function NewsletterSection({
  value,
  inherited,
  onChange,
  canEdit,
  scope,
  idPrefix,
}: {
  /** What this level sets, where null means it takes the answer from below. */
  value: NewsletterOptIn | null;
  /** What it would be if this level stopped setting it, and which layer answered. */
  inherited: Inherited<NewsletterOptIn>;
  onChange: (value: NewsletterOptIn | null) => void;
  canEdit: boolean;
  scope: 'org' | 'brand';
  idPrefix: string;
}) {
  const overridden = value !== null;
  const shown = value ?? inherited.value;

  return (
    <Stack gap="sm">
      <Stack component="ul" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        <OverrideField
          label="Opt in mode"
          overridden={overridden}
          inheritedFrom={inherited.from}
          canEdit={canEdit}
          onOverride={() => onChange(inherited.value)}
          onInherit={() => onChange(null)}
        >
          <NativeSelect
            id={`${idPrefix}-newsletter-opt-in`}
            label="Opt in mode"
            description={
              scope === 'brand'
                ? 'What happens when a reader gives this brand their email address at the end of an interaction.'
                : 'What happens when a reader gives one of your brands their email address at the end of an interaction.'
            }
            disabled={!canEdit || !overridden}
            value={shown}
            onChange={(event) => onChange(event.target.value === 'single' ? 'single' : 'double')}
            data={[
              { value: 'double', label: OPT_IN_LABELS.double },
              { value: 'single', label: OPT_IN_LABELS.single },
            ]}
            style={{ maxWidth: '28rem' }}
          />
        </OverrideField>
      </Stack>
      <Text size="sm" c="dimmed">
        {shown === 'double'
          ? 'Nobody joins this list without opening an email first, so an address typed by somebody else, or mistyped, never ends up on it. Signups sit as waiting to confirm until then, and some readers never do.'
          : 'Every address joins the list straight away, so nothing is lost between typing and confirming. It also means an address nobody has checked, including one typed by somebody who does not own it.'}
      </Text>
      {scope === 'org' && (
        <Text size="sm" c="dimmed">
          Waiting for a confirmation is what every brand gets until somebody says otherwise, here or
          on the brand itself. A brand that has answered for itself keeps its own answer whatever
          this becomes.
        </Text>
      )}
    </Stack>
  );
}

const OPT_IN_LABELS: Readonly<Record<NewsletterOptIn, string>> = {
  double: 'Double: the address waits until the reader opens a confirmation email',
  single: 'Single: the address joins the list as soon as it is typed',
};
